import { useState } from "react";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { adminApi } from "../../api";
import { useAsync } from "../../hooks";
import { LoadingText } from "../ui";

const DAYS_OPTIONS = [
  { value: 7,  label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
];

interface AnalyticsUser {
  user_id: string;
  full_name: string;
  email: string;
  messages: number;
  conversations: number;
  tokens: number;
  last_active?: string | null;
  [key: string]: unknown;
}

interface AnalyticsTotals {
  messages: number;
  active_users: number;
  avg_messages_per_user: number;
  tokens: number;
  tokens_input: number;
  tokens_output: number;
}

interface AnalyticsDaily {
  date: string;
  messages: number;
  active_users: number;
  tokens: number;
}

interface AnalyticsData {
  users: AnalyticsUser[];
  totals: AnalyticsTotals;
  daily: AnalyticsDaily[];
}

function fmtTokens(n: number): string {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtDateShort(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function KpiCard({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

function SortIcon({ col, sortCol, sortDir }: { col: string; sortCol: string; sortDir: string }) {
  if (sortCol !== col) return <span className="ml-1 text-gray-300">↕</span>;
  return <span className="ml-1">{sortDir === "desc" ? "↓" : "↑"}</span>;
}

export default function UsageAnalytics() {
  const [days, setDays] = useState(30);
  const [sortCol, setSortCol] = useState("messages");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const { data, loading } = useAsync<AnalyticsData>(
    () => adminApi.getAnalytics(days).then((res) => res.data),
    [days],
  );

  function toggleSort(col: string) {
    if (sortCol === col) setSortDir((d) => d === "desc" ? "asc" : "desc");
    else { setSortCol(col); setSortDir("desc"); }
  }

  const sortedUsers = data
    ? [...data.users].sort((a, b) => {
        const diff = ((a[sortCol] as number) ?? 0) < ((b[sortCol] as number) ?? 0) ? -1 : 1;
        return sortDir === "desc" ? -diff : diff;
      })
    : [];

  if (loading) return <LoadingText>Loading analytics…</LoadingText>;
  if (!data) return <p className="text-sm text-red-500">Failed to load analytics.</p>;

  const { totals, daily } = data;

  // Recharts wants short labels — trim to MMM DD
  const chartData = daily.map((d) => ({ ...d, label: fmtDateShort(d.date) }));

  return (
    <div className="space-y-6">
      {/* Day-range picker */}
      <div className="flex items-center gap-2">
        {DAYS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setDays(opt.value)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              days === opt.value
                ? "bg-[#1D345F] text-white"
                : "border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <KpiCard label="Total messages" value={totals.messages.toLocaleString()} />
        <KpiCard label="Active users" value={totals.active_users} />
        <KpiCard
          label="Avg messages / user"
          value={totals.avg_messages_per_user}
          sub={`over ${days} days`}
        />
        <KpiCard
          label="Total tokens"
          value={fmtTokens(totals.tokens)}
          sub={`${fmtTokens(totals.tokens_input)} in · ${fmtTokens(totals.tokens_output)} out`}
        />
        <KpiCard
          label="Tokens / message"
          value={totals.messages ? fmtTokens(Math.round(totals.tokens / totals.messages)) : "—"}
          sub="avg per conversation turn"
        />
      </div>

      {/* Daily messages chart */}
      {chartData.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Daily messages &amp; active users</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={35} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={28} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="messages"
                name="Messages"
                stroke="#1D345F"
                strokeWidth={2}
                dot={false}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="active_users"
                name="Active users"
                stroke="#3b82f6"
                strokeWidth={1.5}
                strokeDasharray="4 2"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Daily token chart */}
      {chartData.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-gray-700">Daily token usage</h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                interval={Math.max(0, Math.floor(chartData.length / 10) - 1)}
              />
              <YAxis tick={{ fontSize: 10 }} width={40} tickFormatter={fmtTokens} />
              <Tooltip formatter={(v) => fmtTokens(v as number)} />
              <Bar dataKey="tokens" name="Tokens" fill="#1D345F" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-user breakdown */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-700">
            Per-user activity{" "}
            <span className="font-normal text-gray-400">({sortedUsers.length} active users)</span>
          </h3>
        </div>
        {sortedUsers.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">No activity in this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-600">User</th>
                  {[
                    { key: "messages",      label: "Messages" },
                    { key: "conversations", label: "Conversations" },
                    { key: "tokens",        label: "Tokens" },
                    { key: "last_active",   label: "Last active" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      className="cursor-pointer whitespace-nowrap px-4 py-2 text-right font-semibold text-gray-600 hover:text-gray-900"
                    >
                      {label}<SortIcon col={key} sortCol={sortCol} sortDir={sortDir} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u, i) => (
                  <tr key={u.user_id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-800">{u.full_name}</div>
                      <div className="text-gray-400">{u.email}</div>
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">{u.messages.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{u.conversations.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{fmtTokens(u.tokens)}</td>
                    <td className="px-4 py-2 text-right text-gray-500">{fmtDate(u.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
